import { Explain, Hint } from '@/components/Explain';
import { CopyId, Money } from '@/components/primitives';
import { cn } from '@/lib/cn';
import { reconcile, SOURCE_COLUMNS, summarise, type ReconCell } from '@/features/bet-inspection/lib/reconcile';
import type { TimeZoneMode } from '@/lib/format';
import { Timestamp } from '@/components/primitives';
import type { BetBundle } from '@/types';

/**
 * The view betlens exists for.
 *
 * One row per transaction, one column per system. Systems should agree; where
 * they don't, that gap IS the bug. A row present in MySQL but missing from
 * realtime means the copy pipeline dropped it — the money moved, the reporting
 * just doesn't know. No other tool in the platform shows this.
 */
export function ReconciliationMatrix({ bundle, tz }: { bundle: BetBundle; tz: TimeZoneMode }) {
  const rows = reconcile(bundle);
  const { problems, unknownSources, comparedSources } = summarise(rows);

  if (!rows.length) {
    return (
      <p className="px-4 py-3 text-[12px] text-text-dim">
        No transactions to reconcile.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pb-2">
        {problems > 0 ? (
          <span className="font-condensed text-[10px] font-semibold uppercase tracking-wider text-warn">
            {problems} of {rows.length} transactions disagree across sources
          </span>
        ) : (
          <span
            className={cn(
              'font-condensed text-[10px] font-semibold uppercase tracking-wider',
              // Never announce agreement in green when most sources are dark —
              // "everything agrees" among 1 reachable source is not reassurance,
              // it is an absence of evidence.
              unknownSources > 0 ? 'text-text-dim' : 'text-ok',
            )}
          >
            {rows.length} transactions agree across the {comparedSources} source
            {comparedSources === 1 ? '' : 's'} we could reach
          </span>
        )}

        {unknownSources > 0 && (
          <span className="font-condensed text-[10px] font-semibold uppercase tracking-wider text-crit">
            · {unknownSources} source{unknownSources === 1 ? '' : 's'} unreachable — their
            agreement is unknown
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="border-y border-hairline bg-surface-sunken">
              <Th>
                <Explain term="transaction_id">trx</Explain>
              </Th>
              <Th>
                <Explain term="action">action</Explain>
              </Th>
              <Th align="right">amount</Th>
              <Th>time</Th>
              {SOURCE_COLUMNS.map((c) => (
                <Th key={c.key} align="center">
                  <Explain term={c.glossary}>{c.label}</Explain>
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.transactionId}
                className={cn(
                  'border-b border-hairline/60 hover:bg-surface-raised/50',
                  row.hasProblem && 'bg-warn-bg/30',
                )}
              >
                <Td>
                  <CopyId value={row.transactionId} />
                </Td>
                <Td>
                  <ActionChip action={row.action} type={row.type} />
                </Td>
                <Td align="right">
                  <Money
                    amount={row.amount}
                    meta={bundle.currencyMetadata.data}
                    currency={row.currency}
                  />
                </Td>
                <Td>
                  <Timestamp
                    date={row.at}
                    raw={row.at?.toISOString()}
                    mode={tz}
                  />
                </Td>
                {SOURCE_COLUMNS.map((c) => (
                  <Td key={c.key} align="center">
                    <Cell cell={row.cells[c.key]} source={c.label} />
                  </Td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const CELL = {
  present: { glyph: '✓', cls: 'text-ok', label: 'matches' },
  mismatch: { glyph: '≠', cls: 'text-warn', label: 'disagrees' },
  missing: { glyph: '✗', cls: 'text-crit', label: 'missing' },
  unknown: { glyph: '?', cls: 'text-text-faint', label: 'unknown' },
} as const;

function Cell({ cell, source }: { cell: ReconCell; source: string }) {
  const m = CELL[cell.state];

  return (
    <Hint
      content={
        <div className="space-y-1">
          <div className={cn('eyebrow', m.cls)}>
            {source} — {m.label}
          </div>
          <div className="text-[11.5px] text-text-dim">{describe(cell)}</div>
          {cell.diffs.length > 0 && (
            <ul className="space-y-0.5 border-t border-hairline pt-1 font-mono text-[11px] text-warn">
              {cell.diffs.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
          {cell.occurrences > 1 && (
            <div className="border-t border-hairline pt-1 text-[11px] text-text-dim">
              Logged {cell.occurrences} times — this source records every attempt,
              including failures.
            </div>
          )}
        </div>
      }
    >
      <span className={cn('cursor-help font-mono', m.cls)}>
        {m.glyph}
        {cell.occurrences > 1 && (
          <sup className="ml-0.5 text-[9px] text-text-faint">{cell.occurrences}</sup>
        )}
      </span>
    </Hint>
  );
}

function describe(cell: ReconCell): string {
  switch (cell.state) {
    case 'present':
      return 'Present here and consistent with the authoritative record.';
    case 'mismatch':
      return 'Present, but the values do not match the Primary Database.';
    case 'missing':
      return 'This source was reached and does not contain this transaction. If it should, the pipeline that feeds it has dropped data.';
    case 'unknown':
      return 'This source could not be reached, so we cannot say whether the transaction is there.';
  }
}

function ActionChip({ action, type }: { action: string; type: string }) {
  const cls =
    action === 'win'
      ? 'text-ok'
      : action === 'rollback'
        ? 'text-warn'
        : 'text-info';
  return (
    <span className="flex items-baseline gap-1">
      <span className={cn('font-condensed text-[10px] font-semibold uppercase tracking-wider', cls)}>
        {action}
      </span>
      {type && type !== action && (
        <span className="font-mono text-[10px] text-text-faint">{type}</span>
      )}
    </span>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      className={cn(
        'eyebrow px-3 py-1.5 font-semibold',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      className={cn(
        'px-3 py-1.5',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
      )}
    >
      {children}
    </td>
  );
}
